import { useState, useSyncExternalStore } from 'react';
import type { AvailableSlot } from '@/hooks/usePublicBooking';
import { PortalIcon } from '@/components/portal/PortalIcon';
import { daySlotsToShow, MOBILE_DAY_SLOT_CAP, uniqueSlotsByClock } from '@/lib/portalBookingSlots';
import {
  clinicCivilDate,
  clockFromIso,
  formatPortalDayHeading,
  formatPortalWeekRange,
  isoWeekNumber,
} from '@/lib/portalFormat';
import { cn } from '@/utils/cn';

function subscribeCompact(onStoreChange: () => void) {
  const media = window.matchMedia('(max-width: 767px)');
  media.addEventListener('change', onStoreChange);
  return () => media.removeEventListener('change', onStoreChange);
}

function compactPortalSlots() {
  return window.matchMedia('(max-width: 767px)').matches;
}

export function WeekSlotPicker({
  days,
  weekMonday,
  slotsByDate,
  closedDates,
  selectedStart,
  onSelect,
  onPrevWeek,
  onNextWeek,
  canGoPrev,
  canGoNext = true,
  today,
}: {
  days: string[];
  weekMonday: string;
  slotsByDate: Map<string, AvailableSlot[]>;
  closedDates?: Set<string>;
  selectedStart: string | null;
  onSelect: (slot: AvailableSlot) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  canGoPrev: boolean;
  canGoNext?: boolean;
  today?: string;
}) {
  const weekEnd = days[days.length - 1] ?? weekMonday;
  const todayCivil = today ?? clinicCivilDate(new Date());
  const compact = useSyncExternalStore(subscribeCompact, compactPortalSlots, () => false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  return (
    <>
      <div className="wk">
        <button
          type="button"
          className="btn btn-secondary btn-icon"
          aria-label="Forrige uge"
          disabled={!canGoPrev}
          onClick={onPrevWeek}
        >
          <PortalIcon name="chevronLeft" />
        </button>
        <div className="lbl2">
          <b>Uge {isoWeekNumber(weekMonday)}</b>
          <span>{formatPortalWeekRange(weekMonday, weekEnd)}</span>
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-icon"
          aria-label="Næste uge"
          disabled={!canGoNext}
          onClick={onNextWeek}
        >
          <PortalIcon name="chevronRight" />
        </button>
      </div>
      <div className="days">
        {days.map((civil) => {
          const past = civil < todayCivil;
          const closed = !past && (closedDates?.has(civil) ?? false);
          const unique = past || closed ? [] : uniqueSlotsByClock(slotsByDate.get(civil) ?? []);
          const selectedIndex = unique.findIndex((slot) => slot.start === selectedStart);
          const open =
            expanded[civil] === true ||
            (selectedIndex >= 0 && compact && selectedIndex >= MOBILE_DAY_SLOT_CAP);
          const { shown, hidden } = daySlotsToShow(unique, compact, open);
          return (
            <div key={civil} className={cn('day', past && 'past', closed && 'closed')}>
              <div className="hd-row">
                <div className="dh">{formatPortalDayHeading(civil)}</div>
                <div className="ds">
                  {past ? 'Tidligere' : closed ? 'Lukket' : `${unique.length} ledige`}
                </div>
              </div>
              {past ? (
                <div className="none">Dagen er passeret.</div>
              ) : closed ? (
                <div className="none">Klinikken holder lukket.</div>
              ) : unique.length === 0 ? (
                <div className="none">Ingen ledige tider.</div>
              ) : (
                <div className="slots">
                  {shown.map((slot) => {
                    const on = selectedStart === slot.start;
                    return (
                      <button
                        key={slot.start}
                        type="button"
                        className={cn('slot', 'num', on && 'on')}
                        aria-pressed={on}
                        onClick={() => onSelect(slot)}
                      >
                        {clockFromIso(slot.start)}
                      </button>
                    );
                  })}
                  {hidden > 0 ? (
                    <button
                      type="button"
                      className="slot more"
                      onClick={() => setExpanded((current) => ({ ...current, [civil]: true }))}
                    >
                      +{hidden} flere
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
